/**
 * Echo Hunt -- Settings tab: the judge loop's knobs (P10, §9.1-§9.3, §5.5).
 *
 * Three groups, in the order they matter:
 *
 *  1. **The band** (§9.2). The one number that decides whether a round is hard enough. It is TWO-SIDED,
 *     and the panel says why: a judge scoring BELOW the band is confidently inverted, which tells a reader
 *     just as much as being right. The panel also shows the `2X(1-X)` chance ceiling for the current X, so
 *     the AUC band is never read as if it were the miscategorization % (§9.2).
 *  2. **The loop's judges** (§9.2). A list of models, one call each. Two is the floor -- a single noisy
 *     judge would flip a round's difficulty on its own.
 *  3. **The repairs and the held-out judge** (§9.3, §9.5, §5.5). The attempt cap, the per-slot rewrite
 *     budget, rejection sampling (off by default, and it says why), and the held-out model, which is the
 *     only honest leakage estimate the feature has.
 *
 * Everything writes straight through to `echoProfiles.judge`, like the other settings panels: these are
 * cheap values and a separate Save button would let the readouts below drift out of sync with them.
 */
import React, {useState} from "react";
import {observer_mgl} from "mobx-graphlink";
import {Button, CheckBox, Column, Row, Text, TextInput} from "react-vcomponents";
import {InfoButton} from "web-vcore";
import {store} from "../../../Store/index.js";
import {LLMApi} from "../../../Store/main/settings.js";
import {GetMyEchoProfile, SetJudgeSettings_Live, DEFAULT_X_SHARE} from "../../../EchoHunt/Profiles_Live.js";
import {GetJudgeSettings} from "../../../EchoHunt/Profiles_Live.js";
import {DefaultEchoJudgeSettings, NormalizeBand, DescribeRepair} from "../../../EchoHunt/Judge.js";
import {RandomJudgeMiscategorizationPct} from "../../../EchoHunt/Scoring.js";
import {NumberField} from "./NumberField.js";

export const JudgePanel = observer_mgl(()=>{
	const profile = GetMyEchoProfile();
	const settings = store.main.settings;
	const globalModel = settings.llm_mainAPI == LLMApi.openRouter ? settings.openRouterModel : settings.anthropicModel;

	if (profile == null) {
		return <Text style={{opacity: .7}}>Create an Echo Hunt profile to configure the judge loop.</Text>;
	}

	const judge = GetJudgeSettings();
	const band = NormalizeBand(judge.bandMin, judge.bandMax);
	const xShare = profile.xShare ?? DEFAULT_X_SHARE;
	const chanceCeiling = RandomJudgeMiscategorizationPct(xShare);

	const change = (updates: Partial<typeof judge>)=>{
		SetJudgeSettings_Live(updates).catch(ex=>console.error("Failed to save judge settings:", ex));
	};

	return (
		<>
			<Row>
				<Text style={{fontWeight: "bold"}}>The judge loop</Text>
				<InfoButton ml={5} text={[
					"Before a round is playable, a stranger -- a model that has never seen your dreams and cannot",
					"reach your journal -- scores every bullet. The system knows the truth, so it measures how well",
					"that stranger did. If it did too well, the round is REPAIRED and re-judged.",
					"",
					"The number is AUC: the chance that a random real bullet is rated above a random fake one.",
					"0.5 is a coin flip, 1.0 is perfect separation. Whatever you score above the judge is your own",
					"familiarity, which is the whole point of the game.",
					"",
					"The band is two-sided on purpose. A judge scoring BELOW it is confidently wrong, which tells a",
					"reader as much about which bullets are yours as being right would.",
				].join("\n")}/>
			</Row>

			{/* the band (§9.2) */}
			<Row mt={8} center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{width: 120}}>AUC band:</Text>
				<NumberInput value={judge.bandMin} step={.05} min={0} max={1}
					onChange={value=>change({bandMin: value})}/>
				<Text>to</Text>
				<NumberInput value={judge.bandMax} step={.05} min={0} max={1}
					onChange={value=>change({bandMax: value})}/>
				<Text style={{opacity: .6, fontSize: 12}}>
					{band.min == judge.bandMin && band.max == judge.bandMax
						? `currently ${band.min.toFixed(2)} - ${band.max.toFixed(2)}`
						: `saved as ${band.min.toFixed(2)} - ${band.max.toFixed(2)} (reversed input was swapped)`}
				</Text>
			</Row>

			<Text mt={5} style={{fontSize: 12, opacity: .7}}>
				{`At X = ${(xShare * 100).toFixed(0)}% real, a judge with no information reaches ${chanceCeiling.toFixed(0)}% miscategorization.`
					+ " That ceiling is why the band is on AUC and not on the percentage: the percentage means something different at every X."}
			</Text>

			{/* the loop's judges (§9.2) */}
			<Row mt={12}>
				<Text style={{fontWeight: "bold"}}>Loop judges</Text>
				<InfoButton ml={5} text={[
					"One judge is one set of fingerprints, so a single call can flip a round's difficulty on its own.",
					"Every model listed here gets one call per attempt, and EVERY one of them must land inside the band.",
					"",
					"Leave the list empty to call the `judge` role's configured model twice (the cheap form of the",
					"same idea). Suggested: two different model families, so their tells cannot coincide.",
				].join("\n")}/>
			</Row>
			<JudgeModelList models={judge.judgeModels ?? []} globalModel={globalModel}
				onChange={models=>change({judgeModels: models})}/>

			{/* repairs + hard stop (§9.3, §9.4) */}
			<Row mt={12} center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold", width: 150}}>Repair attempts:</Text>
				<NumberInput value={judge.maxAttempts} step={1} min={1} max={8}
					onChange={value=>change({maxAttempts: Math.max(1, Math.round(value))})}/>
				<Text style={{fontSize: 12, opacity: .65}}>
					{`rounds before the hard stop (§9.4); each costs ${Math.max(2, (judge.judgeModels ?? []).length)} judge call(s)`}
				</Text>
			</Row>
			<Row mt={6} center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold", width: 150}}>Rewrites per bullet:</Text>
				<NumberInput value={judge.rewriteBudgetPerSlot} step={1} min={0} max={6}
					onChange={value=>change({rewriteBudgetPerSlot: Math.max(0, Math.round(value))})}/>
				<Text style={{fontSize: 12, opacity: .65}}>
					{`before the slot is RESAMPLED instead (§9.3). Budget 0 = always resample.`}
				</Text>
			</Row>

			{/* rejection sampling (§5.5) */}
			<Row mt={12} center style={{gap: 10, flexWrap: "wrap"}}>
				<CheckBox text="Rejection-sample each fake against the held-out judge" value={judge.rejectionSampling}
					onChange={value=>change({rejectionSampling: value})}/>
				<NumberInput value={judge.rejectionSamplingCandidates} step={1} min={2} max={6}
					onChange={value=>change({rejectionSamplingCandidates: Math.max(2, Math.round(value))})}/>
				<Text style={{fontSize: 12, opacity: .65}}>candidates, keeping the one the judge rates most human</Text>
			</Row>
			<Text mt={3} style={{fontSize: 12, opacity: .7}}>
				{"Off by default: it multiplies the paid generator calls per fake, and nobody has measured whether it helps yet."
					+ " Turn it on to try it; the round log records which candidates were discarded."}
			</Text>

			{/* held-out judge (§9.5) */}
			<Row mt={12} center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold", width: 150}}>Held-out judge:</Text>
				<Text style={{opacity: .85}}>
					{(profile.modelRoles?.heldOutJudge ?? "").trim().length > 0
						? profile.modelRoles!.heldOutJudge!
						: `uses the "Held-out judge" role's model (${globalModel || "(unset)"})`}
				</Text>
				<Text style={{fontSize: 12, opacity: .65}}>
					set it on the per-role models panel above; use a DIFFERENT model family from the loop judges
				</Text>
			</Row>
			<Text mt={3} style={{fontSize: 12, opacity: .7}}>
				{"The held-out judge runs once on the final story and is never fed back into the loop -- it is the only"
					+ " honest leakage estimate, because a judge the loop has been repaired against is no longer a stranger."}
			</Text>

			<Row mt={12} center style={{gap: 10}}>
				<Button text="Reset to defaults" onClick={()=>{
					SetJudgeSettings_Live(DefaultEchoJudgeSettings()).catch(ex=>console.error("Failed to reset judge settings:", ex));
				}}/>
				<Text style={{fontSize: 12, opacity: .6}}>
					{`defaults: band ${DefaultEchoJudgeSettings().bandMin}-${DefaultEchoJudgeSettings().bandMax}, `
						+ `${DefaultEchoJudgeSettings().maxAttempts} attempts, `
						+ `${DefaultEchoJudgeSettings().rewriteBudgetPerSlot} rewrites per bullet, sampling off`}
				</Text>
			</Row>

			<Text mt={10} style={{fontSize: 12, opacity: .6}}>
				{`Repairs the loop can choose: ${(["resampleFake", "resampleReal", "rewriteReal"] as const).map(DescribeRepair).join(" / ")}.`}
			</Text>
		</>
	);
});

// the model list
// ==========

const JudgeModelList = observer_mgl((props: {models: string[], globalModel: string, onChange: (models: string[])=>void})=>{
	const {models, globalModel, onChange} = props;
	const [draft, setDraft] = useState("");

	const add = ()=>{
		const model = draft.trim();
		if (model.length == 0) return;
		// duplicates would mean the same fingerprints twice, which buys nothing over one call
		if (models.some(a=>a.toLowerCase() == model.toLowerCase())) {
			setDraft("");
			return;
		}
		onChange([...models, model]);
		setDraft("");
	};

	return (
		<Column mt={6} style={{gap: 4}}>
			{models.length == 0 &&
				<Text style={{fontSize: 12, opacity: .7}}>
					{`(empty -- the "Judge" role's model is called twice per attempt: ${globalModel || "(unset)"})`}
				</Text>}
			{models.map((model, index)=>
				<Row key={`${model}-${index}`} center style={{gap: 8}}>
					<Text style={{opacity: .5, width: 20, fontSize: 12}}>{index + 1}.</Text>
					<Text style={{width: 280}}>{model}</Text>
					<Button text="Remove" onClick={()=>onChange(models.filter((_, i)=>i != index))}/>
				</Row>,
			)}
			<Row center style={{gap: 8, marginTop: 4}}>
				<TextInput style={{width: 280}} value={draft} placeholder="model id, e.g. anthropic/claude-3.5-sonnet"
					onChange={setDraft}/>
				<Button text="Add" enabled={draft.trim().length > 0} onClick={add}/>
			</Row>
		</Column>
	);
});

// a small numeric input
// ==========

/** Kept as a local alias of the shared `NumberField` (P11 moved the implementation out, because
 * `SuspicionPanel` needs the identical commit-on-blur behaviour and two copies would drift). The alias
 * avoids renaming ~8 call sites in this file for no benefit. */
const NumberInput = NumberField;
